import { FilterLocation, Partner } from "@/data/partner_offering";
import { buildPathWithParams, useMediaQuery } from "@/hooks";
import { useAnalytics } from "@/lib/analytics/analytics";
import { useI18n } from "@/lib/i18n/i18n_provider";
import { MeetingRoomsFilter } from "@/offering/template_offering_listing_meeting_room";
import React from "react";
import { Button } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { useLocationPicker } from "../../hooks/use_location_picker";
import { useLocationPickerState } from "../../hooks/use_location_picker_state";
import { colors } from "../colors";
import { LocationFilterDropdown } from "./location_filter_dropdown";
import { LocationFilterListItem } from "./location_filter_list_item";
import {
  LocationFilterCity,
  LocationPickerDropdown,
} from "../location_picker_dropdown";
import { MobileFilter } from "./mobile_filters";
import classNames from "classnames";

interface LocationFilterProps {
  partner: Partner;
  filters: MobileFilter;
  locations: FilterLocation[];
  displayedValue?: string;
  values: Required<Pick<MeetingRoomsFilter, "cities" | "locationIDs">>;
  onSelectCity: (city: string, locationIDs: string[]) => void;
  onSelectLocation: (locationId: string, fromCity: LocationFilterCity) => void;
  onClearSelection: () => void;
  isMultiSelect?: boolean;
  highlightBorder?: boolean;
}

export function LocationFilter({
  partner,
  filters,
  locations,
  displayedValue,
  values,
  onClearSelection,
  onSelectCity,
  onSelectLocation,
  isMultiSelect = true,
  highlightBorder,
}: LocationFilterProps) {
  const { fbt } = useI18n();
  const { device } = useMediaQuery();
  const analytics = useAnalytics();

  const history = useHistory();

  const { showWhereDropdown, searchInputRef, groupedCities, handleToggle } =
    useLocationPicker(partner, locations);

  const handleSeeResults = () => {
    const pathWithParams = buildPathWithParams(
      window.location.pathname,
      isMultiSelect
        ? {
            cities: values.cities.length ? values.cities.toString() : undefined,
            locationIDs: values.locationIDs.length
              ? values.locationIDs.toString()
              : undefined,
          }
        : {
            city: values.cities[0],
            locationID: values.locationIDs[0],
          }
    );
    history.replace(pathWithParams);
    handleToggle(false);

    if (values.cities.length || values.locationIDs.length)
      analytics.event(
        "Location Search",
        {
          "Partner UUID": partner.id,
          "Partner Name": partner.name,
          "Partner Internal Name": partner.internalName,
          Cities: values.cities,
          Locations: values.locationIDs.map((locationId) => ({
            locationId,
            locationName: filters.location?.options.find(
              (o) => o.value === locationId
            )?.label,
          })),
          Source: "WL",
        },
        { onlyProviders: ["mixpanel", "customerio"] }
      );
  };

  return groupedCities ? (
    <LocationFilterDropdown
      iconName="location"
      label={fbt("Where")}
      value={displayedValue}
      placeholder={fbt("Anywhere")}
      show={showWhereDropdown}
      testID="where"
      highlightBorder={highlightBorder}
      onToggle={(show) => {
        handleToggle(show);
        if (!show) {
          handleSeeResults();
        }
      }}
      dropdownElement={
        <div
          style={{
            width: 330,
            height: "100%",
            maxHeight: device === "mobile" ? "60vh" : "40vh",
          }}
          className="d-flex flex-column"
        >
          <div className="px-3 pt-4 overflow-scroll">
            <LocationPickerDropdown
              cities={groupedCities}
              selectedCities={values.cities}
              selectedLocationIDs={values.locationIDs}
              onSelectCity={onSelectCity}
              onSelectLocation={onSelectLocation}
              searchInputRef={searchInputRef}
              isMultiSelect={isMultiSelect}
            />
          </div>
          <div
            className={classNames(
              "px-3 py-4 d-flex align-items-center justify-content-between",
              values.locationIDs.length
                ? "clear-selection-button-enabled"
                : "clear-selection-button-disabled"
            )}
          >
            <div
              role="button"
              style={
                isMultiSelect
                  ? {
                      color: values.locationIDs.length
                        ? "var(--bs-primary)"
                        : colors.blackMinus60,
                    }
                  : undefined
              }
              onClick={onClearSelection}
            >
              {fbt("Clear selection")}
            </div>
            <Button
              disabled={values.locationIDs.length === 0 && !displayedValue}
              onClick={handleSeeResults}
            >
              {fbt("See results")}
            </Button>
          </div>
        </div>
      }
    />
  ) : null;
}

interface LocationFilterMobileProps {
  partner: Partner;
  locations: FilterLocation[];
  values: Pick<MeetingRoomsFilter, "cities" | "locationIDs">;
  onChangeLocations: (cities: string[], locationIDs: string[]) => void;
  isMultiSelect?: boolean;
  highlightBorder?: boolean;
}

export function LocationFilterMobile({
  partner,
  locations,
  values,
  onChangeLocations,
  isMultiSelect = true,
  highlightBorder,
}: LocationFilterMobileProps) {
  const { fbt } = useI18n();

  const { showWhereDropdown, searchInputRef, groupedCities, handleToggle } =
    useLocationPicker(partner, locations);

  const {
    displayedValue,
    selectedCities,
    selectedLocationIDs,
    handleSelectCity,
    handleSelectLocation,
    handleClearSelection,
  } = useLocationPickerState(locations, values);

  const handleClickDone = () => {
    onChangeLocations(selectedCities, selectedLocationIDs);
    handleToggle(false);
  };

  return groupedCities ? (
    <LocationFilterListItem
      label={fbt("Where")}
      value={displayedValue}
      placeholder={fbt("Anywhere")}
      show={showWhereDropdown}
      testID="where"
      highlightBorder={highlightBorder}
      onToggle={(show) => {
        handleToggle(show);
        if (!show) {
          handleClickDone();
        }
      }}
      dropdownElement={
        <div
          style={{
            width: 330,
            height: "100%",
            maxHeight: "60vh",
          }}
          className="d-flex flex-column"
        >
          <div className="px-3 pt-4 overflow-scroll">
            <LocationPickerDropdown
              cities={groupedCities}
              selectedCities={selectedCities}
              selectedLocationIDs={selectedLocationIDs}
              onSelectCity={handleSelectCity}
              onSelectLocation={handleSelectLocation}
              searchInputRef={searchInputRef}
              isMultiSelect={isMultiSelect}
            />
          </div>
          <div className="px-3 py-4 d-flex align-items-center justify-content-between">
            <div
              role="button"
              style={{
                color: selectedLocationIDs.length
                  ? "var(--bs-primary)"
                  : colors.blackMinus60,
              }}
              onClick={handleClearSelection}
            >
              {fbt("Clear selection")}
            </div>
            <Button
              disabled={selectedLocationIDs.length === 0 && !displayedValue}
              style={{ width: "fit-content" }}
              onClick={handleClickDone}
            >
              {fbt("Done")}
            </Button>
          </div>
        </div>
      }
    />
  ) : null;
}
